import { Lens } from '@dhmk/zustand-lens';
import type { RichTextDoc } from '@lib/rich-text';

import { eventTimezoneForLocalClock } from '@/features/events/utils/event-timezones';

export type EventDayForm = {
  /** The row's React identity, which an unsaved day has no `id` to provide. */
  key: string;
  /** The saved day this row edits, so a replacement keeps its bookings. Null until it is saved. */
  id: string | null;
  date: string;
  startTime: string;
  /** Empty until the admin picks one. */
  endTime: string;
};

export const DEFAULT_EVENT_START_TIME = '09:00';

export const newEventDayForm = (): EventDayForm => ({
  key: crypto.randomUUID(),
  id: null,
  date: '',
  startTime: DEFAULT_EVENT_START_TIME,
  endTime: '',
});

export type EventStepForms = {
  id: string | null;
  /** The Hosted by choice: `gamma`, `personal`, or an entity id. */
  host: string;
  name: string;
  subtitle: string;
  days: EventDayForm[];
  timezone: string;
  venueName: string;
  venueAddress: string;
  arrivalNote: string;
  googleMapUrl: string;
  about: RichTextDoc | null;
  coverFileId: string | null;
  /**
   * The picked cover before the event exists. The presigned upload is issued against the event, so
   * a file chosen on Basics can only be uploaded once the POST has returned an id.
   */
  coverFile: File | null;
  /** Carries the pick into the dirty check, which compares serialized forms and cannot see a File. */
  coverFileName: string;
  /** The saved event's cover, shown as the preview until a new file is picked. */
  coverUrl: string | null;
  maxInviteesPerTableBooking: number;
  /** Slot lengths, in minutes, the event offers for table bookings. */
  offeredBookingDurations: number[];
  attendeeDirectoryEnabled: boolean;
  isPrivate: boolean;
  listedInGammaCatalogue: boolean;
  isUpcomingOnly: boolean;
};

const FALLBACK_TIMEZONE = 'UTC';

export const defaultEventTimezone = (): string => eventTimezoneForLocalClock() ?? FALLBACK_TIMEZONE;

export const EMPTY_EVENT_STEP_FORMS: EventStepForms = {
  id: null,
  host: '',
  name: '',
  subtitle: '',
  days: [{ key: 'day-1', id: null, date: '', startTime: DEFAULT_EVENT_START_TIME, endTime: '' }],
  timezone: defaultEventTimezone(),
  venueName: '',
  venueAddress: '',
  arrivalNote: '',
  googleMapUrl: '',
  about: null,
  coverFileId: null,
  coverFile: null,
  coverFileName: '',
  coverUrl: null,
  maxInviteesPerTableBooking: 3,
  offeredBookingDurations: [15, 30, 60],
  attendeeDirectoryEnabled: true,
  isPrivate: false,
  listedInGammaCatalogue: false,
  isUpcomingOnly: false,
};

export type EventCreateSlice = {
  currentStep: number;
  stepForms: EventStepForms;
  savedStepForms: EventStepForms | null;
  validatedSteps: number[];
  /**
   * A cover upload started on Basics and not yet settled. The presigned PUT is only half of it:
   * the upload resolves once the file service reports the file stored, and a save sent inside that
   * window would carry the cover id the event had before the pick.
   */
  isCoverUploading: boolean;
  setStep: (_step: number) => void;
  updateStepForm: (_data: Partial<EventStepForms>) => void;
  markSaved: () => void;
  setCoverUploading: (_isUploading: boolean) => void;
  reset: () => void;
};

export const createEventCreateStore: Lens<EventCreateSlice> = (set) => ({
  currentStep: 1,
  stepForms: EMPTY_EVENT_STEP_FORMS,
  savedStepForms: null,
  validatedSteps: [],
  isCoverUploading: false,
  setStep: (step) =>
    set((state) => {
      const leftStep = state.currentStep;
      state.currentStep = step;

      if (step > leftStep && !state.validatedSteps.includes(leftStep)) {
        state.validatedSteps = [...state.validatedSteps, leftStep];
      }
    }),
  updateStepForm: (data) =>
    set((state) => {
      state.stepForms = { ...state.stepForms, ...data };
    }),
  markSaved: () =>
    set((state) => {
      state.savedStepForms = state.stepForms;
    }),
  setCoverUploading: (isUploading) =>
    set((state) => {
      state.isCoverUploading = isUploading;
    }),
  reset: () =>
    set((state) => {
      state.currentStep = 1;
      state.stepForms = EMPTY_EVENT_STEP_FORMS;
      state.savedStepForms = null;
      state.validatedSteps = [];
      state.isCoverUploading = false;
    }),
});
